import { Banner, Box, Button, Spinner, Text, Tooltip, useToast } from '@color/continuum';
import igv, { type Browser, type CreateOpt } from 'igv';
import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom-v5-compat';

import { AlignmentStatusBanner } from './AlignmentStatusBanner';
import {
  useIgvConfigQuery,
  useSampleSequenceRunIgvAlignmentQuery,
  useSampleSequenceRunIgvStatusQuery,
} from './api';
import { applyTrackPrefs, useIgvPrefs } from './igvPrefs';

// Effectively-unbounded cap for the reads track so autoHeight always expands
// to fit every read instead of falling back to igv's internal scrollbar.
const READS_TRACK_MAX_HEIGHT = 1_000_000;

// Vertical space (in px) to leave below the browser so the page footer
// (© Color Health, Inc. + links) stays on screen instead of being pushed off.
const FOOTER_RESERVE = 180;

// Floor for the browser height so it stays usable on short viewports even if
// that means the footer requires a small scroll.
const MIN_BROWSER_HEIGHT = 480;

// igv.js navigates the browser and resolves to a boolean indicating whether the
// locus was found. Its bundled types mistakenly declare `search` as returning
// `void`, so we re-type it here to read the result.
type LocusSearch = (locus: string) => Promise<boolean>;

// igv.js accepts a browser `File` object as a track's `url` at runtime (it reads
// it via FileReader and infers the format from the filename), but its bundled
// types only allow a string url, so we re-type loadTrack for the local-file case.
type LocalTrackLoad = (
  config: { url: File; name: string } & Record<string, unknown>
) => Promise<unknown>;

// Non-indexed feature formats igv can read from a single local file (no
// companion index needed). The picker's `accept` filter and the tooltip's
// human-readable list are both derived from this single source of truth.
const LOCAL_TRACK_EXTENSIONS = [
  'bed',
  'gff',
  'gff3',
  'gtf',
  'bedgraph',
  'wig',
  'narrowpeak',
  'broadpeak',
  'vcf',
  'seg',
  'gwas',
];

const LOCAL_TRACK_ACCEPT = LOCAL_TRACK_EXTENSIONS.map((extension) => `.${extension}`).join(',');

const LOCAL_TRACK_TYPES_LABEL = `Supported file types: ${LOCAL_TRACK_EXTENSIONS.join(', ')}`;

// igv's 'featuresloaded' event hands back the viewport whose data just loaded.
// This event (and its payload) isn't in igv's published type unions, so we
// describe just the shape we read and subscribe through a narrow cast.
type FeaturesLoadedViewport = { trackView?: { track?: { name?: string } } };
type FeaturesLoadedEmitter = {
  on: (event: 'featuresloaded', handler: (viewport: FeaturesLoadedViewport) => void) => void;
  off: (event: 'featuresloaded', handler: (viewport: FeaturesLoadedViewport) => void) => void;
};

// igv disables the crosshair cursor guide and the center line while in
// whole-genome view (the state the browser starts in before we navigate to a
// locus), flipping their stored flags off. These internals aren't in igv's
// published types, so we reach them through a narrow cast.
type GuideButton = { setState: (active: boolean) => void };
type GuideControls = {
  doShowCursorGuide: boolean;
  doShowCenterLine: boolean;
  setCursorGuideVisibility: (visible: boolean) => void;
  setCenterLineVisibility: (visible: boolean) => void;
  navbar?: { cursorGuideButton?: GuideButton; centerLineButton?: GuideButton };
};

// Turn the crosshair and center line on, re-asserting the state igv cleared
// during its whole-genome init so they stay visible at the navigated locus.
const enableCursorAndCenterGuides = (browser: Browser) => {
  const guides = browser as unknown as GuideControls;
  guides.doShowCursorGuide = true;
  guides.doShowCenterLine = true;
  guides.setCursorGuideVisibility(true);
  guides.setCenterLineVisibility(true);
  guides.navbar?.cursorGuideButton?.setState(true);
  guides.navbar?.centerLineButton?.setState(true);
};

export const View = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showErrorToast } = useToast();
  const [browserHeight, setBrowserHeight] = useState<number | undefined>(undefined);
  const [searchParams] = useSearchParams();
  const locus = searchParams.get('locus');
  const ssrId = searchParams.get('ssr_id');

  const { data: config } = useIgvConfigQuery();
  const { prefsRef, persistPrefs } = useIgvPrefs();

  // Whether the run's alignment file is available, archived, restoring, etc.
  // Only load the reads track when it is actually available; an archived file
  // would otherwise fail to load (and we surface a banner instead).
  const statusQuery = useSampleSequenceRunIgvStatusQuery(ssrId ?? undefined);
  const status = statusQuery.data?.status;
  const shouldLoadAlignment = status === 'available';
  const runNotFound = statusQuery.error?.status === 404;

  // igv.js can't resolve every locus query param; we navigate to it explicitly
  // after creating the browser so we can show a banner when it isn't found.
  const [hasInvalidLocus, setHasInvalidLocus] = useState(false);
  const [browser, setBrowser] = useState<Browser | null>(null);

  // True from when we start loading the reads track until igv reports its reads
  // have actually rendered. This covers the gap after the fetch resolves where
  // igv is still downloading/indexing the alignment with nothing yet visible.
  const [isAppendingReads, setIsAppendingReads] = useState(false);

  const alignmentQuery = useSampleSequenceRunIgvAlignmentQuery(
    ssrId ?? undefined,
    shouldLoadAlignment
  );

  // The reference genome renders as soon as its config loads; the reads track
  // (gated on a status check, then a fetch) streams in afterwards. This shows a
  // small indicator while we are still resolving and fetching those reads.
  const isLoadingReads =
    Boolean(ssrId) &&
    !runNotFound &&
    (statusQuery.isLoading ||
      (shouldLoadAlignment && (alignmentQuery.isLoading || isAppendingReads)));

  // Size the browser to fill the space from its top (below the nav bars) to the
  // viewport bottom, reserving room for the footer. Measured from live layout so
  // it adapts to nav/footer height changes.
  // nosemgrep:semgrep-rules.ui-useEffect
  useEffect(() => {
    const updateHeight = () => {
      const top = rootRef.current?.getBoundingClientRect().top ?? 0;
      setBrowserHeight(Math.max(MIN_BROWSER_HEIGHT, window.innerHeight - top - FOOTER_RESERVE));
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  // Create the browser as soon as the genome config is available so the
  // reference renders immediately, independent of the reads track.
  // nosemgrep:semgrep-rules.ui-useEffect
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !config) {
      return undefined;
    }

    setHasInvalidLocus(false);

    const { tracks, ...reference } = config;
    // Seed the reference/config tracks and browser-level settings with the user's
    // stored prefs so they render with the preferred view from the first paint.
    const prefs = prefsRef.current;
    const tracksWithPrefs = tracks.map((track) => applyTrackPrefs(track, prefs));
    let createdBrowser: Browser | undefined;
    let isCancelled = false;
    igv
      .createBrowser(container, {
        reference,
        tracks: tracksWithPrefs,
        ...prefs.browser,
      } as CreateOpt)
      .then(async (newBrowser) => {
        if (isCancelled) {
          igv.removeBrowser(newBrowser); // clean up even if we already unmounted
          return;
        }
        createdBrowser = newBrowser;
        setBrowser(newBrowser);
        if (locus) {
          // igv.search resolves to `false` only when the locus can't be
          // recognized. Once it recognizes the locus it navigates there and
          // then loads track/sequence data; a failure in that data load rejects
          // the promise even though the locus is already shown. So a rejection
          // means "loaded fine", not "invalid locus" — only `false` is invalid.
          const search = newBrowser.search.bind(newBrowser) as unknown as LocusSearch;
          const locusFound = await search(locus).catch(() => true);
          if (!isCancelled && !locusFound) {
            setHasInvalidLocus(true);
          }
        }
        if (!isCancelled) {
          enableCursorAndCenterGuides(newBrowser);
        }
      })
      .catch(() => {});

    return () => {
      isCancelled = true;
      setBrowser(null);
      if (createdBrowser) {
        // Save the user's settings before igv disposes the browser (removeBrowser
        // calls dispose, after which the session can't be read).
        persistPrefs(createdBrowser);
        igv.removeBrowser(createdBrowser);
      }
    };
    // prefsRef is a ref, read at creation time, so it isn't a dependency.
  }, [config, locus, persistPrefs, prefsRef]);

  // Append the reads track once it has been fetched, without recreating the
  // browser (which would reset the user's navigation).
  // nosemgrep:semgrep-rules.ui-useEffect
  useEffect(() => {
    const alignmentTrack = alignmentQuery.data;
    if (!browser || !shouldLoadAlignment || !alignmentTrack) {
      return;
    }
    if (browser.findTracks('name', alignmentTrack.name).length > 0) {
      return;
    }
    setIsAppendingReads(true);

    // loadTrack resolves once the track is added, before its reads have
    // downloaded and rendered, so keep the indicator up until igv's
    // 'featuresloaded' event reports the reads for this track are ready.
    const emitter = browser as unknown as FeaturesLoadedEmitter;
    const handleFeaturesLoaded = (viewport: FeaturesLoadedViewport) => {
      if (viewport.trackView?.track?.name === alignmentTrack.name) {
        setIsAppendingReads(false);
      }
    };
    emitter.on('featuresloaded', handleFeaturesLoaded);

    // igv.js has no mouse-wheel handler, so its alignment track is only
    // scrollable via its own scrollbar. autoHeight (with the maxHeight cap
    // lifted, since it otherwise defaults to >=1000px) expands the track to fit
    // every read and drops that scrollbar, leaving the container's native wheel
    // scroll as the only scroll.
    browser
      .loadTrack(
        applyTrackPrefs(
          { ...alignmentTrack, autoHeight: true, maxHeight: READS_TRACK_MAX_HEIGHT },
          prefsRef.current
        )
      )
      .catch(() => setIsAppendingReads(false));

    return () => emitter.off('featuresloaded', handleFeaturesLoaded);
    // prefsRef is a ref, read when the track loads, so it isn't a dependency.
  }, [browser, shouldLoadAlignment, alignmentQuery.data, prefsRef]);

  // Persist the user's settings on reload/tab close. igv mutates them outside
  // React, so we read them back from the live browser here; the write is
  // synchronous so it completes before the page unloads.
  // nosemgrep:semgrep-rules.ui-useEffect
  useEffect(() => {
    if (!browser) {
      return undefined;
    }
    const handleBeforeUnload = () => persistPrefs(browser);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [browser, persistPrefs]);

  // Load a user-selected local file (e.g. a BED file) as a new track. igv reads
  // the File directly and infers the format from its name, so this stays fully
  // client-side with no upload.
  const handleAddLocalTrack = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so re-selecting the same file fires another change event.
    event.target.value = '';
    if (!browser || !file) {
      return;
    }
    if (browser.findTracks('name', file.name).length > 0) {
      showErrorToast(`A track named “${file.name}” is already loaded.`);
      return;
    }
    const loadLocalTrack = browser.loadTrack.bind(browser) as unknown as LocalTrackLoad;
    // Seed the file's track with the stored prefs for its kind (e.g. all BED
    // files), keyed off the filename since igv only infers the format on load.
    await loadLocalTrack(applyTrackPrefs({ url: file, name: file.name }, prefsRef.current)).catch(
      () => showErrorToast(`Could not load track “${file.name}”.`)
    );
  };

  return (
    <Box
      ref={rootRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        // PageContainer is flow-layout, so `height: 100%` would collapse; use the
        // measured height (the fallback only applies for the first paint).
        height: browserHeight ?? MIN_BROWSER_HEIGHT,
        minHeight: MIN_BROWSER_HEIGHT,
        gap: 1,
      }}
    >
      {runNotFound && (
        <Banner severity="warning" variant="in-component">
          This sample sequence run could not be found, so reads are not shown.
        </Banner>
      )}
      {ssrId && status != null && <AlignmentStatusBanner ssrId={ssrId} status={status} />}
      {hasInvalidLocus && (
        <Banner severity="warning" variant="in-component">
          Could not navigate to “{locus}”. Check that the locus or gene name is valid.
        </Banner>
      )}
      {isLoadingReads && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ position: 'relative', width: 20, height: 20 }}>
            <Spinner variant="small" />
          </Box>
          <Text variant="body2" color="textSecondary">
            Loading reads…
          </Text>
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Tooltip title={LOCAL_TRACK_TYPES_LABEL}>
          {/* span wrapper so the tooltip still shows while the button is disabled */}
          <Box component="span">
            <Button
              variant="color-secondary"
              size="small"
              disabled={!browser}
              onClick={() => fileInputRef.current?.click()}
            >
              Add track
            </Button>
          </Box>
        </Tooltip>
        <input
          ref={fileInputRef}
          data-testid="add-track-file-input"
          type="file"
          accept={LOCAL_TRACK_ACCEPT}
          hidden
          onChange={handleAddLocalTrack}
        />
      </Box>
      <Box ref={containerRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }} />
    </Box>
  );
};
